import React, { useEffect, useMemo, useState } from 'react';
import { leaveAPI, workRequestAPI } from '../services/api';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { Bar, BarChart, CartesianGrid, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from 'recharts';
import { ArrowUpDown, CalendarOff, Plane } from 'lucide-react';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from './ui/table-controls';
import { chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const LeaveSection = ({ currentTab = 'history', initialLeaveType = 'casual' }) => {
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [wfhRequests, setWfhRequests] = useState([]);
  const [outstationRequests, setOutstationRequests] = useState([]);
  const [mutationWindowHours, setMutationWindowHours] = useState(24);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [formData, setFormData] = useState({
    leave_type: initialLeaveType,
    start_date: '',
    end_date: '',
    reason: '',
    half_day_period: '',
    hours: '',
    start_time: ''
  });
  const [durationType, setDurationType] = useState('full_day'); // full_day, half_day, hours

  const statusChartData = useMemo(() => {
    const counts = leaves.reduce((acc, leave) => {
      const key = (leave.status || 'pending').toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return [
      { name: 'Approved', value: counts.approved || 0, color: '#10B981' },
      { name: 'Pending', value: counts.pending || 0, color: '#F59E0B' },
      { name: 'Rejected', value: counts.rejected || 0, color: '#EF4444' },
    ].filter((entry) => entry.value > 0);
  }, [leaves]);

  const monthlyLeaveData = useMemo(() => {
    const monthly = {};
    leaves.forEach((leave) => {
      if (!leave?.start_date) return;
      const month = format(new Date(leave.start_date), 'MMM');
      monthly[month] = (monthly[month] || 0) + Number(leave.total_days || 0);
    });
    return Object.entries(monthly).map(([month, days]) => ({ month, days }));
  }, [leaves]);

  const upcomingHolidays = useMemo(() => {
    const today = new Date();
    return holidays
      .filter((holiday) => new Date(holiday.date) >= new Date(today.getFullYear(), today.getMonth(), today.getDate()))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [holidays]);

  const getStatusBadgeVariant = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'approved') return 'success';
    if (normalized === 'pending') return 'warning';
    if (normalized === 'rejected') return 'danger';
    return 'outline';
  };

  const leaveTable = useTableControls(leaves, {
    initialSortKey: 'start_date',
    initialSortDirection: 'desc',
  });

  const renderSortableHeader = (label, key) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-medium hover:text-primary"
      onClick={() => leaveTable.onSort(key)}
    >
      {label}
      <ArrowUpDown className="h-3.5 w-3.5" />
    </button>
  );

  const allLeaveRelatedRequests = useMemo(() => {
    const items = [];

    for (const r of leaves || []) {
      items.push({
        id: `leave-${r.id}`,
        kind: 'leave',
        requestId: r.id,
        title: `${String(r.leave_type || 'leave').replace(/_/g, ' ')}`,
        dateLabel: r.start_date && r.end_date ? `${r.start_date} → ${r.end_date}` : '-',
        status: r.status || 'pending',
        createdAt: r.created_at || '',
        raw: r,
      });
    }

    for (const r of regularizations || []) {
      items.push({
        id: `regularization-${r.id}`,
        kind: 'regularization',
        requestId: r.id,
        title: 'regularization',
        dateLabel: r.attendance_date || '-',
        status: r.status || 'pending',
        createdAt: r.created_at || '',
        raw: r,
      });
    }

    for (const r of wfhRequests || []) {
      items.push({
        id: `wfh-${r.id}`,
        kind: 'wfh',
        requestId: r.id,
        title: 'work from home',
        dateLabel: r.from_date && r.to_date ? `${r.from_date} → ${r.to_date}` : '-',
        status: r.status || 'pending',
        createdAt: r.created_at || '',
        raw: r,
      });
    }

    for (const r of outstationRequests || []) {
      items.push({
        id: `outstation-${r.id}`,
        kind: 'outstation',
        requestId: r.id,
        title: 'outstation duty',
        dateLabel: r.from_date && r.to_date ? `${r.from_date} → ${r.to_date}` : '-',
        status: r.status || 'pending',
        createdAt: r.created_at || '',
        raw: r,
      });
    }

    return items.sort((a, b) => {
      const at = new Date(a.createdAt || 0).getTime();
      const bt = new Date(b.createdAt || 0).getTime();
      return bt - at;
    });
  }, [leaves, outstationRequests, regularizations, wfhRequests]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-fetch only on tab change; fetchData is stable
  }, [currentTab]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, leave_type: initialLeaveType || 'casual' }));
  }, [initialLeaveType]);

  useEffect(() => {
    if (durationType === 'half_day' || durationType === 'hours') {
        setFormData(prev => ({ ...prev, end_date: prev.start_date }));
    }
  }, [durationType, formData.start_date]);

  const fetchData = async () => {
    try {
      const [leavesRes, balanceRes, holidaysRes] = await Promise.all([
        leaveAPI.getMyLeaves(),
        leaveAPI.getBalance(),
        leaveAPI.getHolidays(new Date().getFullYear())
      ]);
      setLeaves(leavesRes.data);
      setBalance(balanceRes.data);
      setHolidays(holidaysRes.data);

      if (currentTab === 'requests') {
        const [regRes, wfhRes, outRes] = await Promise.all([
          workRequestAPI.getMyRegularizations().catch(() => ({ data: [] })),
          workRequestAPI.getMyWfh().catch(() => ({ data: [] })),
          workRequestAPI.getMyOutstation().catch(() => ({ data: [] })),
        ]);
        setRegularizations(regRes.data || []);
        setWfhRequests(wfhRes.data || []);
        setOutstationRequests(outRes.data || []);

        const windowRes = await leaveAPI.getRequestMutationWindowHours().catch(() => ({ data: { hours: 24 } }));
        const hours = Number(windowRes?.data?.hours);
        setMutationWindowHours(Number.isFinite(hours) && hours > 0 ? hours : 24);
      }
    } catch (error) {
      console.error("Error fetching leave data", error);
    }
  };

  const parseDateValue = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    let normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    normalized = normalized.replace(/(\.\d{3})\d+/, '$1');
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) return parsed;
    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  const isWithinMutationWindow = (createdAtValue) => {
    const created = parseDateValue(createdAtValue);
    if (!created) return false;
    return Date.now() - created.getTime() <= mutationWindowHours * 60 * 60 * 1000;
  };

  const canCancelRequest = (row) => {
    const status = String(row?.status || '').toLowerCase();
    return isWithinMutationWindow(row?.createdAt) && ['pending', 'pending_level2', 'escalated'].includes(status);
  };

  const canEditRequest = (row) => {
    const status = String(row?.status || '').toLowerCase();
    return isWithinMutationWindow(row?.createdAt) && status === 'pending';
  };

  const startEdit = (row) => {
    const raw = row?.raw || {};
    if (row.kind === 'leave') {
      setEditForm({
        start_date: raw.start_date ? String(raw.start_date).slice(0, 10) : '',
        end_date: raw.end_date ? String(raw.end_date).slice(0, 10) : '',
        reason: raw.reason || '',
        mutation_reason: '',
      });
    } else if (row.kind === 'regularization') {
      setEditForm({
        attendance_date: raw.attendance_date ? String(raw.attendance_date).slice(0, 10) : '',
        requested_check_in: raw.requested_check_in || '',
        requested_check_out: raw.requested_check_out || '',
        reason: raw.reason || '',
        mutation_reason: '',
      });
    } else if (row.kind === 'wfh') {
      setEditForm({
        from_date: raw.from_date ? String(raw.from_date).slice(0, 10) : '',
        to_date: raw.to_date ? String(raw.to_date).slice(0, 10) : '',
        reason: raw.reason || '',
        work_location: raw.work_location || '',
        work_plan: raw.work_plan || '',
        mutation_reason: '',
      });
    } else {
      setEditForm({
        from_date: raw.from_date ? String(raw.from_date).slice(0, 10) : '',
        to_date: raw.to_date ? String(raw.to_date).slice(0, 10) : '',
        destination: raw.destination || '',
        purpose: raw.purpose || '',
        travel_mode: raw.travel_mode || '',
        contact_details: raw.contact_details || '',
        reason: raw.reason || '',
        mutation_reason: '',
      });
    }
    setEditingRowId(row.id);
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setEditForm({});
  };

  const handleCancelRequest = async (row) => {
    const reason = window.prompt('Enter cancel reason');
    if (!reason) return;
    try {
      if (row.kind === 'leave') {
        await leaveAPI.cancel(row.requestId, reason);
      } else if (row.kind === 'regularization') {
        await workRequestAPI.cancelRegularization(row.requestId, reason);
      } else if (row.kind === 'wfh') {
        await workRequestAPI.cancelWfh(row.requestId, reason);
      } else {
        await workRequestAPI.cancelOutstation(row.requestId, reason);
      }
      toast.success('Request cancelled');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel request'));
    }
  };

  const handleSaveEdit = async (row) => {
    try {
      if (!editForm.mutation_reason) {
        toast.error('Mutation reason is required');
        return;
      }
      if (row.kind === 'leave') {
        await leaveAPI.edit(row.requestId, {
          start_date: editForm.start_date,
          end_date: editForm.end_date,
          reason: editForm.reason,
          mutation_reason: editForm.mutation_reason,
        });
      } else if (row.kind === 'regularization') {
        await workRequestAPI.editRegularization(row.requestId, {
          attendance_date: editForm.attendance_date,
          requested_check_in: editForm.requested_check_in || null,
          requested_check_out: editForm.requested_check_out || null,
          reason: editForm.reason,
          mutation_reason: editForm.mutation_reason,
        });
      } else if (row.kind === 'wfh') {
        await workRequestAPI.editWfh(row.requestId, {
          from_date: editForm.from_date,
          to_date: editForm.to_date,
          reason: editForm.reason,
          work_location: editForm.work_location || null,
          work_plan: editForm.work_plan || null,
          mutation_reason: editForm.mutation_reason,
        });
      } else {
        await workRequestAPI.editOutstation(row.requestId, {
          from_date: editForm.from_date,
          to_date: editForm.to_date,
          destination: editForm.destination,
          purpose: editForm.purpose,
          travel_mode: editForm.travel_mode || null,
          contact_details: editForm.contact_details || null,
          reason: editForm.reason,
          mutation_reason: editForm.mutation_reason,
        });
      }
      toast.success('Request updated');
      cancelEdit();
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update request'));
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    try {
      const payload = {
          leave_type: formData.leave_type,
          start_date: formData.start_date,
          end_date: (durationType === 'half_day') ? formData.start_date : formData.end_date,
          reason: formData.reason
      };
      
      if (durationType === 'half_day') {
          payload.half_day_period = formData.half_day_period;
      }
      if (durationType === 'hours') {
          payload.hours = parseFloat(formData.hours);
          if (formData.start_time) {
              payload.start_time = formData.start_time;
          }
      }

      await leaveAPI.apply(payload);
      toast.success("Leave application submitted");
      fetchData();
      setFormData({ leave_type: initialLeaveType || 'casual', start_date: '', end_date: '', reason: '', half_day_period: '', hours: '', start_time: '' });
      setDurationType('full_day');
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to apply for leave"));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Balance Cards - Show for history and apply */}
      {(currentTab === 'history' || currentTab === 'apply') && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 animate-slide-up">
          <StatCard
            label="Annual Leave"
            value={balance?.annual_leave || 0}
            icon={CalendarOff}
            tone="primary"
            onClick={() => setFormData((prev) => ({ ...prev, leave_type: 'annual' }))}
          />
          <StatCard
            label="Casual Leave"
            value={balance?.casual_leave || 0}
            icon={CalendarOff}
            tone="info"
            onClick={() => setFormData((prev) => ({ ...prev, leave_type: 'casual' }))}
          />
          <StatCard
            label="Sick Leave"
            value={balance?.sick_leave || 0}
            icon={CalendarOff}
            tone="purple"
            onClick={() => setFormData((prev) => ({ ...prev, leave_type: 'sick' }))}
          />
          <StatCard
            label="Comp Off"
            value={balance?.compensatory_leave || 0}
            icon={Plane}
            tone="warning"
            onClick={() => setFormData((prev) => ({ ...prev, leave_type: 'compensatory' }))}
          />
        </div>
      )}

      {currentTab === 'requests' && (
        <div className="space-y-4 animate-slide-up">
          <div className="flex items-center justify-between">
            <h3 className="text-hr-lg font-semibold">Requests</h3>
            <Badge variant="outline">{allLeaveRelatedRequests.length} requests</Badge>
          </div>
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-hr-lg">All Leave Related Requests</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-hr-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Created</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allLeaveRelatedRequests.flatMap((row) => {
                      const rows = [];
                      rows.push(
                        <tr key={row.id} className="border-b hover:bg-muted/40">
                          <td className="p-3 capitalize">{row.title}</td>
                          <td className="p-3">{row.dateLabel}</td>
                          <td className="p-3">
                            <Badge variant={getStatusBadgeVariant(row.status)}>
                              {String(row.status || 'pending').toUpperCase()}
                            </Badge>
                          </td>
                          <td className="p-3">{row.createdAt ? String(row.createdAt) : '-'}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {canEditRequest(row) && (
                                <Button size="sm" variant="outline" onClick={() => startEdit(row)}>
                                  Edit
                                </Button>
                              )}
                              {canCancelRequest(row) && (
                                <Button size="sm" variant="destructive" onClick={() => handleCancelRequest(row)}>
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );

                      if (editingRowId === row.id) {
                        rows.push(
                          <tr key={`${row.id}-edit`} className="border-b bg-muted/20">
                            <td colSpan={5} className="p-4">
                              <div className="grid gap-3 md:grid-cols-4">
                                {row.kind === 'leave' && (
                                  <>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Start Date</div>
                                      <input
                                        type="date"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.start_date || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, start_date: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">End Date</div>
                                      <input
                                        type="date"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.end_date || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, end_date: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1 md:col-span-2">
                                      <div className="text-xs text-muted-foreground">Reason</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.reason || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, reason: e.target.value }))}
                                      />
                                    </div>
                                  </>
                                )}

                                {row.kind === 'regularization' && (
                                  <>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Attendance Date</div>
                                      <input
                                        type="date"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.attendance_date || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, attendance_date: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Requested Check-in</div>
                                      <input
                                        type="datetime-local"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.requested_check_in || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, requested_check_in: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Requested Check-out</div>
                                      <input
                                        type="datetime-local"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.requested_check_out || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, requested_check_out: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Reason</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.reason || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, reason: e.target.value }))}
                                      />
                                    </div>
                                  </>
                                )}

                                {row.kind === 'wfh' && (
                                  <>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">From Date</div>
                                      <input
                                        type="date"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.from_date || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, from_date: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">To Date</div>
                                      <input
                                        type="date"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.to_date || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, to_date: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Work Location</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.work_location || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, work_location: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Reason</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.reason || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, reason: e.target.value }))}
                                      />
                                    </div>
                                  </>
                                )}

                                {row.kind === 'outstation' && (
                                  <>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">From Date</div>
                                      <input
                                        type="date"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.from_date || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, from_date: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">To Date</div>
                                      <input
                                        type="date"
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.to_date || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, to_date: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Destination</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.destination || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, destination: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Purpose</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.purpose || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, purpose: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Travel Mode</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.travel_mode || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, travel_mode: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <div className="text-xs text-muted-foreground">Contact Details</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.contact_details || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, contact_details: e.target.value }))}
                                      />
                                    </div>
                                    <div className="space-y-1 md:col-span-2">
                                      <div className="text-xs text-muted-foreground">Reason</div>
                                      <input
                                        className="w-full rounded-md border bg-background p-2 text-sm"
                                        value={editForm.reason || ''}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, reason: e.target.value }))}
                                      />
                                    </div>
                                  </>
                                )}

                                <div className="space-y-1 md:col-span-4">
                                  <div className="text-xs text-muted-foreground">Mutation Reason</div>
                                  <input
                                    className="w-full rounded-md border bg-background p-2 text-sm"
                                    value={editForm.mutation_reason || ''}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, mutation_reason: e.target.value }))}
                                  />
                                </div>
                              </div>
                              <div className="mt-3 flex gap-2">
                                <Button size="sm" onClick={() => handleSaveEdit(row)}>
                                  Save
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEdit}>
                                  Close
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return rows;
                    })}
                    {allLeaveRelatedRequests.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-6">
                          <EmptyState compact title="No requests found" description="Your leave and work requests will show up here." icon={CalendarOff} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Tab */}
      {currentTab === 'history' && (
        <div className="space-y-6 animate-slide-up">
          <div className="flex justify-between items-center">
            <h3 className="text-hr-lg font-semibold">My Leaves</h3>
            <Badge variant="outline">{leaves.length} requests</Badge>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-hr-lg">Status Split</CardTitle>
              </CardHeader>
              <CardContent className="h-[260px]">
                {statusChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusChartData} dataKey="value" cx="50%" cy="50%" innerRadius={55} outerRadius={90}>
                        {statusChartData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipContentStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <EmptyState compact title="No leaves yet" description="Your leave status split will appear here." icon={CalendarOff} />
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-hr-lg">Monthly Leave Days</CardTitle>
              </CardHeader>
              <CardContent className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyLeaveData}>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="month" {...axisProps} />
                    <YAxis allowDecimals={false} {...axisProps} />
                    <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                    <Bar dataKey="days" fill={chartColor(0)} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-hr-lg">Leave History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-hr-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">{renderSortableHeader('Type', 'leave_type')}</th>
                      <th className="text-left p-3">{renderSortableHeader('Duration', 'start_date')}</th>
                      <th className="text-left p-3">{renderSortableHeader('Days', 'total_days')}</th>
                      <th className="text-left p-3">{renderSortableHeader('Reason', 'reason')}</th>
                      <th className="text-left p-3">{renderSortableHeader('Status', 'status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveTable.data.map((leave) => (
                      <tr key={leave.id} className="border-b hover:bg-muted/40">
                        <td className="p-3 capitalize">{leave.leave_type} Leave</td>
                        <td className="p-3">
                          {format(new Date(leave.start_date), 'MMM dd')} - {format(new Date(leave.end_date), 'MMM dd, yyyy')}
                        </td>
                        <td className="p-3">{leave.total_days}</td>
                        <td className="p-3 max-w-[280px] truncate">{leave.reason || '-'}</td>
                        <td className="p-3">
                          <Badge variant={getStatusBadgeVariant(leave.status)}>
                            {String(leave.status || 'pending').toUpperCase()}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {leaves.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-6">
                          <EmptyState compact title="No leave history" description="Approved and past leaves will appear here." icon={CalendarOff} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
            {leaves.length > 0 && (
              <TableControls
                totalRows={leaveTable.totalRows}
                page={leaveTable.page}
                totalPages={leaveTable.totalPages}
                pageSize={leaveTable.pageSize}
                pageSizes={leaveTable.pageSizes}
                onPageSizeChange={leaveTable.onPageSizeChange}
                onPrevious={leaveTable.goToPreviousPage}
                onNext={leaveTable.goToNextPage}
                exportConfig={{
                  title: 'Leave History',
                  fileName: `leave-history-${new Date().toISOString().split('T')[0]}`,
                  rows: leaves,
                  columns: [
                    { label: 'Type', accessor: (row) => `${row.leave_type || ''} Leave` },
                    { label: 'Start Date', accessor: (row) => row.start_date || '' },
                    { label: 'End Date', accessor: (row) => row.end_date || '' },
                    { label: 'Days', accessor: (row) => row.total_days ?? '' },
                    { label: 'Reason', accessor: (row) => row.reason || '' },
                    { label: 'Status', accessor: (row) => row.status || '' },
                  ],
                }}
              />
            )}
          </Card>
        </div>
      )}

      {/* Apply Tab */}
      {currentTab === 'apply' && (
        <div className="grid gap-6 xl:grid-cols-3">
          <Card className="hover-lift animate-slide-up xl:col-span-2">
            <CardHeader><CardTitle>Apply for Leave</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleApply} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-hr-sm font-medium">Leave Type</label>
                    <select
                      className="w-full p-2 border rounded-md focus:border-primary focus:ring-1 focus:ring-ring transition-colors bg-background"
                      value={formData.leave_type}
                      onChange={(e) => setFormData({...formData, leave_type: e.target.value})}
                    >
                      <option value="casual">Casual Leave</option>
                      <option value="sick">Sick Leave</option>
                      <option value="annual">Annual Leave</option>
                      <option value="maternity">Maternity Leave</option>
                      <option value="work_from_home">Work From Home</option>
                      <option value="outstation_duty">Out Station Duty</option>
                      <option value="regularization">Regularization</option>
                      <option value="compensatory">Comp Off</option>
                    </select>
                    {formData.leave_type === 'compensatory' && (
                      <p className="text-xs text-muted-foreground">
                        Available comp off balance: {balance?.compensatory_leave || 0}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-hr-sm font-medium">Duration Type</label>
                    <select
                      className="w-full p-2 border rounded-md focus:border-primary focus:ring-1 focus:ring-ring transition-colors bg-background"
                      value={durationType}
                      onChange={(e) => setDurationType(e.target.value)}
                    >
                      <option value="full_day">Full Day(s)</option>
                      <option value="half_day">Half Day</option>
                      <option value="hours">Hourly (Fractional)</option>
                    </select>
                  </div>
                </div>

                {durationType === 'half_day' && (
                  <div className="space-y-2">
                    <label className="text-hr-sm font-medium">Half Day Period</label>
                    <select
                      className="w-full p-2 border rounded-md focus:border-primary focus:ring-1 focus:ring-ring transition-colors bg-background"
                      value={formData.half_day_period}
                      onChange={(e) => setFormData({...formData, half_day_period: e.target.value})}
                      required
                    >
                      <option value="">Select Period</option>
                      <option value="first_half">First Half</option>
                      <option value="second_half">Second Half</option>
                    </select>
                  </div>
                )}

                {durationType === 'hours' && (
                  <div className="space-y-2">
                    <label className="text-hr-sm font-medium">Hours</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      className="w-full p-2 border rounded-md focus:border-primary focus:ring-1 focus:ring-ring transition-colors bg-background"
                      value={formData.hours}
                      onChange={(e) => setFormData({...formData, hours: e.target.value})}
                      required
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-hr-sm font-medium">Start Date</label>
                    <input
                      type="date"
                      className="w-full p-2 border rounded-md focus:border-primary focus:ring-1 focus:ring-ring transition-colors bg-background"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required
                    />
                  </div>
                  {(durationType === 'full_day' || durationType === 'hours') && (
                  <div className="space-y-2">
                    <label className="text-hr-sm font-medium">End Date</label>
                    <input
                      type="date"
                      className="w-full p-2 border rounded-md focus:border-primary focus:ring-1 focus:ring-ring transition-colors bg-background"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      required
                    />
                  </div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-hr-sm font-medium">Reason</label>
                  <textarea
                    className="w-full p-2 border rounded-md focus:border-primary focus:ring-1 focus:ring-ring transition-colors bg-background"
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                    rows={3}
                    required
                  />
                </div>
                <Button type="submit">Submit Application</Button>
              </form>
            </CardContent>
          </Card>
          <Card className="border-border shadow-sm animate-slide-up">
            <CardHeader>
              <CardTitle className="text-hr-lg">Quick Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-hr-sm text-muted-foreground">
              <div className="rounded-md border p-3">
                <p className="font-medium text-foreground">Choose leave type</p>
                <p>Use balance cards above to preselect type quickly.</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="font-medium text-foreground">Use hourly leave</p>
                <p>Select hourly only for short time-off requests.</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="font-medium text-foreground">Comp Off</p>
                <p>Apply against available comp off balance only.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Holidays Tab */}
      {currentTab === 'holidays' && (
        <div className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-hr-lg">Upcoming Holidays</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[360px] overflow-y-auto">
                {upcomingHolidays.slice(0, 8).map((holiday, index) => (
                  <div
                    key={holiday.id}
                    className="p-3 flex gap-3 items-start border rounded-lg bg-card hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-muted/50"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="bg-destructive/10 text-destructive p-2 rounded-md text-center min-w-[3.5rem]">
                      <div className="text-xs uppercase font-bold">{format(new Date(holiday.date), 'MMM')}</div>
                      <div className="text-lg font-bold leading-none">{format(new Date(holiday.date), 'dd')}</div>
                    </div>
                    <div>
                      <p className="font-medium text-hr-sm">{holiday.name}</p>
                      <p className="text-xs text-muted-foreground">{format(new Date(holiday.date), 'EEEE')}</p>
                    </div>
                  </div>
                ))}
                {upcomingHolidays.length === 0 && (
                  <EmptyState compact title="No upcoming holidays" description="Scheduled holidays will appear here." icon={CalendarOff} />
                )}
              </CardContent>
            </Card>
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-hr-lg">Holiday Distribution by Month</CardTitle>
              </CardHeader>
              <CardContent className="h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={holidays.reduce((acc, holiday) => {
                      const month = format(new Date(holiday.date), 'MMM');
                      const existing = acc.find((row) => row.month === month);
                      if (existing) {
                        existing.count += 1;
                      } else {
                        acc.push({ month, count: 1 });
                      }
                      return acc;
                    }, [])}
                  >
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="month" {...axisProps} />
                    <YAxis allowDecimals={false} {...axisProps} />
                    <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                    <Bar dataKey="count" fill={chartColor(1)} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveSection;
